import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import logger from 'utils/logger';
import { getIsDevelopment } from 'constants/environment';
import SECTION_NAMES from '../constants/sectionNames';

const QUEUE_KEY = 'FORTRESS_OFFLINE_QUEUE';
const LOG_GROUP = 'OfflineQueue';

const isSameVariables = (a, b) => isEqual(a, b);

export const getQueue = () => {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error reading offline queue', e);
    return [];
  }
};

const LATEST_WINS_KEYS = ['SAVE_SECTIONS_PROGRESS', 'SAVE_FORM_STATE'];

export const addToQueue = (mutationKey, variables) => {
  let queue = getQueue();

  if (LATEST_WINS_KEYS.includes(mutationKey)) {
    // Search backwards to find the MOST RECENT existing item
    let existingIndex = -1;
    for (let i = queue.length - 1; i >= 0; i--) {
      const item = queue[i];
      if (item.mutationKey === mutationKey) {
        let matches = false;
        if (mutationKey === 'SAVE_FORM_STATE') {
          matches = item.variables.portalUserId === variables.portalUserId;
        } else if (mutationKey === 'SAVE_SECTIONS_PROGRESS') {
          matches =
            item.variables.propertyId === variables.propertyId &&
            item.variables.affordaPortalDetailsId ===
              variables.affordaPortalDetailsId;
        }
        if (matches) {
          existingIndex = i;
          break;
        }
      }
    }

    if (existingIndex !== -1) {
      const existing = queue[existingIndex];

      // If the item is currently being sent to the server, we CANNOT overwrite it.
      // When the server request finishes, the Sync Manager will delete this item,
      // and our new variables will be lost. We must create a new queue item instead.
      if (!existing.inFlight) {
        if (mutationKey === 'SAVE_FORM_STATE') {
          const finalId = variables.forms.id || existing.variables.forms.id;

          // Merge sections cumulatively. If the new mutation has an empty section,
          // but the queue has a populated one, we KEEP the populated one.
          const mergedForms = {
            ...existing.variables.forms,
            ...variables.forms,
            ...(finalId && { id: finalId }),
          };

          const sections = Object.values(SECTION_NAMES);
          sections.forEach((s) => {
            const newHasData =
              Object.keys(variables.forms[s]?.formValues || {}).length > 0;
            if (!newHasData && existing.variables.forms[s]) {
              mergedForms[s] = existing.variables.forms[s];
            }
          });

          existing.variables.forms = mergedForms;
          existing.timestamp = Date.now();
          localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
          window.dispatchEvent(new CustomEvent('offlineQueueUpdated'));
          if (getIsDevelopment()) {
            logger(
              LOG_GROUP,
              'Latest-wins overwrite applied',
              {
                mutationKey,
                queueLength: queue.length,
                itemId: existing.id,
              },
              'info',
            );
          }
          return existing;
        }

        if (mutationKey === 'SAVE_SECTIONS_PROGRESS') {
          existing.variables = variables;
          existing.timestamp = Date.now();
          localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
          window.dispatchEvent(new CustomEvent('offlineQueueUpdated'));
          if (getIsDevelopment()) {
            logger(
              LOG_GROUP,
              'Latest-wins overwrite applied',
              {
                mutationKey,
                queueLength: queue.length,
                itemId: existing.id,
              },
              'info',
            );
          }
          return existing;
        }
      }
    }
  }

  const duplicateItem = queue.find(
    (item) =>
      item?.mutationKey === mutationKey &&
      isSameVariables(item?.variables, variables),
  );

  if (duplicateItem) {
    if (getIsDevelopment()) {
      logger(
        LOG_GROUP,
        'Duplicate mutation skipped',
        { mutationKey, queueLength: queue.length },
        'info',
      );
    }
    return duplicateItem;
  }

  const newItem = {
    id: uuidv4(),
    mutationKey,
    variables,
    timestamp: Date.now(),
  };
  localStorage.setItem(QUEUE_KEY, JSON.stringify([...queue, newItem]));
  window.dispatchEvent(new CustomEvent('offlineQueueUpdated'));
  if (getIsDevelopment()) {
    logger(
      LOG_GROUP,
      `Item added to queue`,
      { ...newItem, queueLength: queue.length + 1 },
      'info',
    );
  }
  return newItem;
};

export const removeFromQueue = (id) => {
  const queue = getQueue();
  const filtered = queue.filter((item) => item.id !== id);
  localStorage.setItem(QUEUE_KEY, JSON.stringify(filtered));
};

export const markInFlight = (id, inFlight) => {
  const queue = getQueue();
  const item = queue.find((i) => i.id === id);
  if (item) {
    item.inFlight = inFlight;
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }
};

export const clearQueue = () => {
  localStorage.removeItem(QUEUE_KEY);
};
